import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, FormGroup, Label, Input, Button, Alert } from "reactstrap";

/**
 * "Set Responsibilities" tab — mirrors SimpleButton7_Click flow (Pass 1 §1.10).
 * Visible only when the CR is still in pre-submit state.
 *
 * Sequence the user follows here:
 *   1) Pick Developmental or Semi-Developmental.
 *   2) Tick any "N/A" boxes for Engineering / Materials / Quality.
 *   3) Pick a default employee per department (Sales/Key + Eng + Mat + Quality).
 *   4) Click "Set Defaults" — server wipes all responses and reseeds per the
 *      choices, then returns the seeded rows.
 *
 * Round 2 (CR legacy-parity repair) — UX fix for the "Set Defaults is
 * confusing / maybe not working" report:
 *
 *   • The four "Default for…" dropdowns are now PRE-FILLED from the responses
 *     that are actually in effect on the CR. Previously they were local state
 *     only, so after clicking Set Defaults (or switching tabs) they reset to
 *     "— select —" and it looked like nothing had been saved. We derive the
 *     applied default per department from the seeded response rows (the most
 *     common `responsible` empId in each department's rows) and seed the
 *     dropdown with it. No new DB columns — the value is read back from the
 *     rows the server already returns.
 *
 *   • A per-department "currently applied" summary shows who each department
 *     defaulted to (or N/A), so the result of the operation is visible.
 *
 *   • The legacy overwrite warning (SimpleButton7_Click) and the
 *     "must pick Dev/Semi-Dev" guard are preserved.
 *
 * Props added in Round 2: salesRows, engRows, matRows, qaRows, keyRows —
 * the per-department response arrays already held by the parent form. They
 * are read-only here (used only to derive the applied defaults).
 */
export default function SetResponsibilitiesTab({
    developmental,
    setDevelopmental,
    engineeringNA,
    setEngineeringNA,
    materialsNA,
    setMaterialsNA,
    qualityNA,
    setQualityNA,
    employees,
    onSetDefaults,
    defaultsApplied,
    isSettingDefaults,
    // Round 2 — response rows for deriving the applied default per department.
    keyRows = [],
    salesRows = [],
    engRows = [],
    matRows = [],
    qaRows = [],
}) {
    // Derive the "currently applied" default employee for each department from
    // the seeded response rows. Sales/Key share one default in the legacy flow,
    // so we look at sales first then key.
    // Sales default is derived from the SALES rows only. (It used to include Key
    // rows too, back when Sales and Key shared one default — but Key is now
    // auto-assigned to the author independently, so including Key rows here made
    // the author dominate and silently overwrite the user's Sales selection.)
    const appliedSales = useMemo(
        () => dominantResponsible(salesRows || []),
        [salesRows]
    );
    const appliedEng = useMemo(() => dominantResponsible(engRows), [engRows]);
    const appliedMat = useMemo(() => dominantResponsible(matRows), [matRows]);
    const appliedQa = useMemo(() => dominantResponsible(qaRows), [qaRows]);

    const [defaultSales, setDefaultSales] = useState("");
    const [defaultEng, setDefaultEng] = useState("");
    const [defaultMat, setDefaultMat] = useState("");
    const [defaultQa, setDefaultQa] = useState("");
    const [touchedDevRadio, setTouchedDevRadio] = useState(false);

    // Re-hydrate each dropdown from the applied default whenever the rows change
    // (e.g. after Set Defaults completes and the parent reloads the CR). We only
    // overwrite the local value if the user hasn't typed a different one this
    // session for that field — applied value wins on (re)load.
    useEffect(() => { if (appliedSales != null) setDefaultSales(String(appliedSales)); }, [appliedSales]);
    useEffect(() => { if (appliedEng != null) setDefaultEng(String(appliedEng)); }, [appliedEng]);
    useEffect(() => { if (appliedMat != null) setDefaultMat(String(appliedMat)); }, [appliedMat]);
    useEffect(() => { if (appliedQa != null) setDefaultQa(String(appliedQa)); }, [appliedQa]);

    const empName = (id) => {
        if (id == null || id === "") return null;
        const e = (employees || []).find((x) => String(x.empId) === String(id));
        return e ? e.name : `#${id}`;
    };

    const empOptions = (employees || []).map((e) => (
        <option key={e.empId} value={e.empId}>
            {e.name}
        </option>
    ));

    const handleApply = () => {
        if (!touchedDevRadio && developmental == null) {
            // Mirrors the MessageBox in SimpleButton7_Click — must pick one.
            window.alert("Please select 'Semi Developmental' or 'Developmental'");
            return;
        }
        if (defaultsApplied) {
            const ok = window.confirm(
                "You have already made default employee selections. If you continue, this operation will overwrite all employees with the new selections you have made, and will remove any optional notes you may have entered.\n\nAre you sure you wish to continue?"
            );
            if (!ok) return;
        }
        onSetDefaults({
            developmental: !!developmental,
            engineeringNA,
            materialsNA,
            qualityNA,
            defaultEmployeeSales: defaultSales ? Number(defaultSales) : null,
            defaultEmployeeEngineering: defaultEng ? Number(defaultEng) : null,
            defaultEmployeeMaterials: defaultMat ? Number(defaultMat) : null,
            defaultEmployeeQuality: defaultQa ? Number(defaultQa) : null,
        });
    };

    return (
        <div>
            <Alert color="info" className="mb-3">
                <strong>1)</strong> Choose <em>Developmental</em> or <em>Semi-Developmental</em>.&nbsp;
                <strong>2)</strong> Mark any departments that don&rsquo;t apply.&nbsp;
                <strong>3)</strong> Pick a default employee per active department.&nbsp;
                <strong>4)</strong> Click <em>Set Defaults</em>.
            </Alert>

            {defaultsApplied && (
                <Alert color="success" className="mb-3">
                    <div className="fw-semibold mb-1">
                        <i className="ri-check-double-line me-1" />
                        Defaults are applied to this CR.
                    </div>
                    <div className="small">
                        Currently in effect:&nbsp;
                        <AppliedBadge label="Sales" na={false} name={empName(defaultSales)} />
                        <AppliedBadge label="Key Details" na={false} name="Author (creator)" />
                        <AppliedBadge label="Engineering" na={engineeringNA} name={empName(defaultEng)} />
                        <AppliedBadge label="Materials" na={materialsNA} name={empName(defaultMat)} />
                        <AppliedBadge label="Quality" na={qualityNA} name={empName(defaultQa)} />
                    </div>
                    <div className="small text-muted mt-1">
                        You can fine-tune individual rows on the department tabs. Re-running
                        Set Defaults will overwrite every employee and clear optional notes.
                    </div>
                </Alert>
            )}

            <Row className="mb-3">
                <Col md={6}>
                    <FormGroup tag="fieldset">
                        <legend className="fw-semibold fs-6">Development Type</legend>
                        <div className="d-flex gap-4">
                            <label className="form-check d-flex align-items-center gap-1 mb-0">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    checked={developmental === true}
                                    onChange={() => { setDevelopmental(true); setTouchedDevRadio(true); }}
                                />
                                <span>Developmental</span>
                            </label>
                            <label className="form-check d-flex align-items-center gap-1 mb-0">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    checked={developmental === false}
                                    onChange={() => { setDevelopmental(false); setTouchedDevRadio(true); }}
                                />
                                <span>Semi-Developmental</span>
                            </label>
                        </div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup tag="fieldset">
                        <legend className="fw-semibold fs-6">Departments Not Applicable</legend>
                        <div className="d-flex gap-3 flex-wrap">
                            <NaCheck label="Engineering N/A" checked={engineeringNA} setChecked={setEngineeringNA} />
                            <NaCheck label="Materials N/A" checked={materialsNA} setChecked={setMaterialsNA} />
                            <NaCheck label="Quality N/A" checked={qualityNA} setChecked={setQualityNA} />
                        </div>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Default for Sales</Label>
                        <Input
                            type="select"
                            value={defaultSales}
                            onChange={(e) => setDefaultSales(e.target.value)}
                        >
                            <option value="">— select —</option>
                            {empOptions}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Default for Engineering</Label>
                        <Input
                            type="select"
                            value={defaultEng}
                            onChange={(e) => setDefaultEng(e.target.value)}
                            disabled={engineeringNA}
                        >
                            <option value="">— select —</option>
                            {empOptions}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Default for Materials</Label>
                        <Input
                            type="select"
                            value={defaultMat}
                            onChange={(e) => setDefaultMat(e.target.value)}
                            disabled={materialsNA}
                        >
                            <option value="">— select —</option>
                            {empOptions}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Default for Quality</Label>
                        <Input
                            type="select"
                            value={defaultQa}
                            onChange={(e) => setDefaultQa(e.target.value)}
                            disabled={qualityNA}
                        >
                            <option value="">— select —</option>
                            {empOptions}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex justify-content-end mt-2">
                <Button color="primary" onClick={handleApply} disabled={isSettingDefaults}>
                    {isSettingDefaults ? "Applying…" : (defaultsApplied ? "Re-apply Defaults" : "Set Defaults")}
                </Button>
            </div>
        </div>
    );
}

/**
 * Most common non-null `responsible` empId across a department's response
 * rows. The seeding stamps the same default on every row in a department, so
 * the dominant value is that default. Returns null when no row has one.
 */
function dominantResponsible(rows) {
    if (!rows || rows.length === 0) return null;
    const counts = new Map();
    for (const r of rows) {
        const v = r?.responsible;
        if (v == null || v === "" || Number(v) === 0) continue;
        const key = String(v);
        counts.set(key, (counts.get(key) || 0) + 1);
    }
    let best = null, bestN = 0;
    for (const [k, n] of counts) {
        if (n > bestN) { best = k; bestN = n; }
    }
    return best == null ? null : Number(best);
}

function AppliedBadge({ label, na, name }) {
    let text, color;
    if (na) { text = "N/A"; color = "secondary"; }
    else if (name) { text = name; color = "success"; }
    else { text = "not set"; color = "warning"; }
    return (
        <span className="me-2">
            <span className="text-muted">{label}:</span>{" "}
            <span className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle`}>
                {text}
            </span>
        </span>
    );
}

function NaCheck({ label, checked, setChecked }) {
    return (
        <label className="form-check d-flex align-items-center gap-1 mb-0">
            <input
                type="checkbox"
                className="form-check-input"
                checked={!!checked}
                onChange={(e) => setChecked(e.target.checked)}
                style={{ accentColor: checked ? "#0d6efd" : undefined }}
            />
            <span style={{ color: checked ? "#0d6efd" : undefined, fontWeight: checked ? 600 : undefined }}>
                {label}
            </span>
        </label>
    );
}
